import { overlaps } from './geometry.js'
import { hopsFor, isLiveVisit } from './travel.js'
import type { BoardVisit, SchedulablePerson } from './types.js'
import { zigzagPlan } from './zigzag.js'

/**
 * Problems the board can name without being asked: late runs, clashes, and
 * vans driving in circles. Missing parts only when the job is already
 * `AWAITING_PARTS` — the materials load is unfinished, so inventing a parts
 * alert from an empty catalog would be a lie.
 */

export type AlertKind = 'late' | 'clash' | 'backtrack' | 'parts'

export interface BoardAlert {
  id: string
  kind: AlertKind
  title: string
  detail: string
  visitId?: string
  userId?: string
}

export function isRunningLate(visit: BoardVisit, now = new Date()): boolean {
  if (!isLiveVisit(visit)) return false
  if (visit.status === 'ON_SITE') return now > visit.endAt
  if (visit.status === 'EN_ROUTE') return now > visit.startAt
  return visit.status === 'CONFIRMED' && now > visit.startAt
}

export function isOutNow(visit: BoardVisit): boolean {
  return visit.status === 'EN_ROUTE' || visit.status === 'ON_SITE'
}

export function isCommercial(visit: { job: { client: { type?: string | null } } }): boolean {
  return visit.job.client.type === 'COMMERCIAL'
}

function clashesOn(visits: BoardVisit[]): BoardVisit[][] {
  const live = visits.filter(isLiveVisit)
  const groups: BoardVisit[][] = []

  for (let i = 0; i < live.length; i += 1) {
    for (let j = i + 1; j < live.length; j += 1) {
      if (overlaps(live[i]!, live[j]!)) groups.push([live[i]!, live[j]!])
    }
  }

  return groups
}

export function boardAlerts(
  visitsByPerson: Map<string, BoardVisit[]>,
  people: SchedulablePerson[],
  now = new Date(),
): BoardAlert[] {
  const alerts: BoardAlert[] = []
  const names = new Map(people.map((person) => [person.id, person.name]))

  for (const [userId, visits] of visitsByPerson) {
    const name = names.get(userId) ?? 'A van'

    for (const visit of visits) {
      if (isRunningLate(visit, now)) {
        alerts.push({
          id: `late:${visit.id}`,
          kind: 'late',
          title: `${name} is running late`,
          detail: visit.job.client.name,
          visitId: visit.id,
          userId,
        })
      }

      if (visit.job.status === 'AWAITING_PARTS') {
        alerts.push({
          id: `parts:${visit.job.id}`,
          kind: 'parts',
          title: 'Waiting on parts',
          detail: visit.job.client.name,
          visitId: visit.id,
          userId,
        })
      }
    }

    for (const pair of clashesOn(visits)) {
      alerts.push({
        id: `clash:${pair[0]!.id}:${pair[1]!.id}`,
        kind: 'clash',
        title: `${name} is double-booked`,
        detail: `${pair[0]!.job.client.name} and ${pair[1]!.job.client.name}`,
        visitId: pair[0]!.id,
        userId,
      })
    }

    const plan = zigzagPlan(visits, userId, now)
    if (plan) {
      alerts.push({
        id: `zigzag:${userId}`,
        kind: 'backtrack',
        title: `${name} is backtracking`,
        detail: `${plan.savingMinutes} minutes recoverable by reordering`,
        userId,
      })
    }
  }

  return alerts
}

